import { IconKey, IconPlus } from '@developerz/ui';
import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { RepoSecretRow } from '../lib/repoSecrets';
import { day } from '../lib/time';
import { EmptyState } from './EmptyState';
import { Panel } from './Panel';
import { StatusPill } from './StatusPill';
import { Button } from './ui/Button';

// Repo detail → Secrets, the presenter half (arc 101 slice 06, doc 06): renders
// exactly what `GET /v1/repos/:owner/:repo/secrets` answers — name, exposure,
// environment, timestamps — and nothing else. `RepoSecretRow` has no value
// field, so there is no prop here that could ever render one. Pure: the
// container (`RepoSecretsTab`) owns the fetch + mutations; this only renders
// props and reports intent up (`onAdd`, `onDeleteRequest`).

export type RepoSecretsViewProps = {
  readonly secrets: readonly RepoSecretRow[];
  readonly onAdd: () => void;
  readonly onDeleteRequest: (row: RepoSecretRow) => void;
};

export function RepoSecretsView(props: RepoSecretsViewProps): JSX.Element {
  return (
    <Panel
      title={t('repo_secrets.title')}
      footer={t('repo_secrets.hint')}
      class="card--footered"
      action={
        <Button size="sm" onClick={props.onAdd}>
          <IconPlus />
          {t('repo_secrets.add')}
        </Button>
      }
    >
      <Show
        when={props.secrets.length > 0}
        fallback={<EmptyState message={t('repo_secrets.empty')} />}
      >
        <ul>
          <For each={props.secrets}>
            {(row) => (
              <li class="row-list__row" data-name={row.name} data-environment={row.environment}>
                <div class="row-list__lead">
                  <span class="row-list__tile">
                    <IconKey />
                  </span>
                  <div class="repo-secrets__lines">
                    <code class="repo-secrets__name">{row.name}</code>
                    <span class="repo-secrets__meta">
                      {t(`repo_secrets.environment_${row.environment}`)} · {day(row.updated_at)}
                    </span>
                  </div>
                </div>
                <div class="repo-secrets__actions">
                  <StatusPill tone={row.exposure === 'materializable' ? 'warning' : 'neutral'}>
                    {t(`repo_secrets.exposure_${row.exposure}`)}
                  </StatusPill>
                  <Button variant="ghost" size="sm" onClick={() => props.onDeleteRequest(row)}>
                    {t('repo_secrets.delete')}
                  </Button>
                </div>
              </li>
            )}
          </For>
        </ul>
      </Show>
    </Panel>
  );
}
